import React, { useState, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ModuleAuditLogsModal from "../components/modals/ModuleAuditLogsModal";
import ResellerLogsModal from "../components/modals/ResellerLogsModal";
import { Shimmer } from "../components/MotionPrimitives";
import apiService from "../services/api";

const Resellers = () => {
  const navigate = useNavigate();
  const [resellers, setResellers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReseller, setSelectedReseller] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentNotes, setCurrentNotes] = useState({ id: null, notes: "" });
  const [_resellerNotes, setResellerNotes] = useState({});
  const [sortField, setSortField] = useState("username");
  const [sortDirection, setSortDirection] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0,
  });
  const [stats, setStats] = useState({
    totalResellers: 0,
    totalRedeemCodes: 0,
    totalUsers: 0,
  });

  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showModuleLogsModal, setShowModuleLogsModal] = useState(false);

  // Disable confirmation modal states
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [resellerToDisable, setResellerToDisable] = useState(null);
  const [isDisabling, setIsDisabling] = useState(false);

  useEffect(() => {
    fetchResellers();
  }, [currentPage, itemsPerPage, sortField, sortDirection, filterStatus]);

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Reset to page 1 when search changes
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchResellers();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchResellers = async () => {
    setLoading(true);
    try {
      // Map frontend sort field to API sort field
      const sortFieldMap = {
        username: "created_at",
        status: "status",
        redeemCodes: "redeem_codes",
        users: "users",
        lastLogin: "created_at",
      };

      const apiSortField = sortFieldMap[sortField] || "created_at";
      const apiSortOrder = sortDirection.toUpperCase();

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sort: apiSortField,
        order: apiSortOrder,
      };

      // Add search param if present
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Add status filter if present
      if (filterStatus && filterStatus !== "all") {
        params.status = filterStatus.toUpperCase();
      }

      const response = await apiService.getResellers(params);
      // The API returns { status: 200, data: { stats, resellers }, pagination: {...} }
      const responseData = response.data || {};
      const resellersData = responseData.resellers || [];
      const statsData = responseData.stats || {
        totalResellers: 0,
        totalRedeemCodes: 0,
        totalUsers: 0,
      };
      const paginationData = response.pagination || {
        total: 0,
        page: 1,
        limit: itemsPerPage,
        pages: 0,
      };

      setResellers(Array.isArray(resellersData) ? resellersData : []);
      setStats(statsData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Error fetching resellers:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to load resellers";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Clear sorting
  const handleClearSort = () => {
    setSortField("username");
    setSortDirection("asc");
    toast.success("Sorting cleared");
  };

  // Display resellers directly from API (already sorted, filtered, and paginated)
  const displayResellers = resellers;
  const currentItems = displayResellers;
  const totalPages = pagination.pages;
  const indexOfFirstItem = (pagination.page - 1) * pagination.limit;
  const indexOfLastItem = Math.min(
    indexOfFirstItem + displayResellers.length,
    pagination.total
  );

  // Handle page changes
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers for pagination UI
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages === 0) return pages;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (pagination.page <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (pagination.page >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = pagination.page - 1; i <= pagination.page + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handleOpenDisableModal = (reseller) => {
    setResellerToDisable(reseller);
    setShowDisableModal(true);
  };

  const handleCloseDisableModal = () => {
    setShowDisableModal(false);
    setResellerToDisable(null);
  };

  const handleConfirmDisable = async () => {
    if (!resellerToDisable) return;

    const newStatus =
      resellerToDisable.status === "inactive" ? "ACTIVE" : "INACTIVE";
    const actionText =
      resellerToDisable.status === "inactive" ? "enable" : "disable";

    setIsDisabling(true);
    try {
      await apiService.updateReseller(resellerToDisable.id, {
        status: newStatus,
      });
      toast.success(`${resellerToDisable.username} has been ${actionText}d`);
      handleCloseDisableModal();
      await fetchResellers(); // Refresh the list
    } catch (error) {
      console.error("Error updating reseller:", error);
      toast.error(`Failed to ${actionText} reseller`);
    } finally {
      setIsDisabling(false);
    }
  };

  const handleSaveNotes = () => {
    setResellerNotes((prev) => ({
      ...prev,
      [currentNotes.id]: currentNotes.notes,
    }));
    toast.success("Notes saved successfully");
    setShowNotesModal(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500";
      case "low":
        return "bg-yellow-500";
      case "inactive":
      case "disabled":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusTooltip = (status) => {
    switch (status) {
      case "active":
        return "Active";
      case "low":
        return "Running low on credits";
      case "inactive":
      case "disabled":
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  // Stats cards using API-provided global stats
  const statsCards = [
    {
      label: "Total Resellers",
      value: stats.totalResellers,
      icon: "fas fa-user-tag",
      color: "bg-blue-500",
    },
    {
      label: "Total Unclaimed Redeem Codes",
      value: stats.totalRedeemCodes.toLocaleString(),
      icon: "fas fa-ticket-alt",
      color: "bg-purple-500",
    },
    {
      label: "Total Users",
      value: stats.totalUsers.toLocaleString(),
      icon: "fas fa-users",
      color: "bg-orange-500",
    },
  ];

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
        <button
          className="btn btn-ghost w-full sm:w-auto flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
          onClick={() => setShowLogsModal(true)}
        >
          <i className="fas fa-history mr-2" />
          <span>Reseller Logs</span>
        </button>
        <button
          className="btn btn-ghost w-full sm:w-auto flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
          onClick={() => setShowModuleLogsModal(true)}
        >
          <i className="fas fa-plug mr-2" />
          <span>Module Audit Logs</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {statsCards.map((stat, index) => (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50"
            initial={{ opacity: 0, y: 20 }}
            key={index}
            style={{
              padding: "16px",
              overflow: "visible",
            }}
            transition={{ delay: index * 0.1 }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                width: "100%",
                minHeight: "40px",
              }}
            >
              <div
                className={`${stat.color} rounded-lg`}
                style={{
                  width: "40px",
                  height: "40px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className={`${stat.icon} text-white`}
                  style={{
                    fontSize: "16px",
                    display: "block",
                  }}
                />
              </div>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <div
                  className="font-bold text-gray-900 dark:text-white"
                  style={{
                    fontSize: "20px",
                    lineHeight: "1.2",
                    display: "block",
                    visibility: "visible",
                    opacity: 1,
                    color: "inherit",
                  }}
                >
                  {stat.value}
                </div>
                <div
                  className="text-gray-600 dark:text-gray-300"
                  style={{
                    fontSize: "12px",
                    marginTop: "2px",
                    display: "block",
                    visibility: "visible",
                    opacity: 1,
                    color: "inherit",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Filters and Search */}
      <div className="card p-6 shadow-md">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
          {/* Search Input */}
          <div className="flex-1">
            <div className="relative">
              <input
                className="input pl-10 w-full"
                placeholder="Search resellers..."
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            {/* Reseller Dropdown */}
            <select
              className="input w-full sm:w-auto"
              value={selectedReseller}
              onChange={(e) => setSelectedReseller(e.target.value)}
            >
              <option value="all">All Resellers</option>
              {[...resellers]
                .sort((a, b) => a.username.localeCompare(b.username))
                .map((reseller) => (
                  <option key={reseller.id} value={reseller.id}>
                    {reseller.username}
                  </option>
                ))}
            </select>

            {/* Status Filter */}
            <select
              className="input w-full sm:w-auto"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>

            {/* Items Per Page */}
            <select
              className="input w-full sm:w-auto"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={500}>500 per page</option>
              <option value={1000}>1000 per page</option>
            </select>

            {/* Clear Sort Button */}
            {(sortField !== "username" || sortDirection !== "asc") && (
              <button
                className="btn btn-ghost text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={handleClearSort}
              >
                <i className="fas fa-times mr-2" />
                Clear Sort
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Resellers Table */}
      <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50">
        <div
          className="overflow-x-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("username")}
                >
                  <div className="flex items-center gap-1">
                    Username
                    {sortField === "username" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Tier
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center justify-center gap-1">
                    Status
                    {sortField === "status" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("redeemCodes")}
                >
                  <div className="flex items-center justify-center gap-1">
                    Unclaimed Codes
                    {sortField === "redeemCodes" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("users")}
                >
                  <div className="flex items-center justify-center gap-1">
                    Users
                    {sortField === "users" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("lastLogin")}
                >
                  <div className="flex items-center gap-1">
                    Last Login
                    {sortField === "lastLogin" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={idx}>
                    {Array.from({ length: 7 }).map((__, cellIdx) => (
                      <td className="px-3 py-3" key={cellIdx}>
                        <Shimmer className="h-3.5 w-full rounded-md" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : displayResellers.length === 0 ? (
                <tr>
                  <td className="text-center py-12 text-gray-500" colSpan="7">
                    No resellers found
                  </td>
                </tr>
              ) : (
                currentItems.map((reseller, index) => (
                  <tr
                    className={`${index % 2 === 0 ? "table-row-striped-even" : "table-row-striped-odd"} table-row-hover`}
                    key={reseller.id}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div
                        className="text-sm font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (reseller.customerId) {
                            navigate(`/customers/${reseller.customerId}`);
                          } else {
                            toast.info(
                              "No customer record linked to this reseller"
                            );
                          }
                        }}
                      >
                        {reseller.username}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {reseller.resellerType === "module"
                          ? "Module"
                          : reseller.tier}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <div className="relative group">
                          <div
                            className={`w-3 h-3 rounded-full ${getStatusColor(reseller.status)}`}
                          />
                          {/* Tooltip */}
                          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
                            <div className="bg-gray-800 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              {getStatusTooltip(reseller.status)}
                            </div>
                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-gray-800 dark:bg-gray-700 rotate-45" />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {reseller.redeemCodes}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {reseller.users}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {reseller.lastLogin}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        {/* View Customer */}
                        <div className="relative group">
                          <button
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            onClick={() => {
                              if (reseller.customerId) {
                                navigate(`/customers/${reseller.customerId}`);
                              } else {
                                toast.info(
                                  "No customer record linked to this reseller"
                                );
                              }
                            }}
                          >
                            <i className="fas fa-edit text-sm" />
                          </button>
                          {/* Tooltip */}
                          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
                            <div className="bg-gray-800 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              View Customer
                            </div>
                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-gray-800 dark:bg-gray-700 rotate-45" />
                          </div>
                        </div>

                        {/* Disable */}
                        <div className="relative group">
                          <button
                            className={`p-2 ${
                              reseller.status === "inactive"
                                ? "text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                                : "text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                            } rounded-lg transition-colors`}
                            onClick={() => handleOpenDisableModal(reseller)}
                          >
                            <i
                              className={`fas ${reseller.status === "inactive" ? "fa-check-circle" : "fa-ban"} text-sm`}
                            />
                          </button>
                          {/* Tooltip */}
                          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
                            <div className="bg-gray-800 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              {reseller.status === "inactive"
                                ? "Enable"
                                : "Disable"}
                            </div>
                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-gray-800 dark:bg-gray-700 rotate-45" />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col lg:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center lg:text-left">
              Showing {indexOfFirstItem + 1} to {indexOfLastItem} of{" "}
              {pagination.total} resellers
            </p>
            <div className="flex gap-2 flex-wrap justify-center">
              <button
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md hover:scale-105"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </button>
              {getPageNumbers().map((page, index) =>
                page === "..." ? (
                  <span
                    className="px-2 py-1 text-sm text-gray-500"
                    key={`ellipsis-${index}`}
                  >
                    ...
                  </span>
                ) : (
                  <button
                    className={`px-3 py-1 text-sm border rounded-lg transition-all duration-200 hover:shadow-md hover:scale-105 ${
                      currentPage === page
                        ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                        : "border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                    key={page}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md hover:scale-105"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notes Modal */}
      <AnimatePresence>
        {showNotesModal && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setShowNotesModal(false)}
          >
            <motion.div
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md"
              exit={{ scale: 0.9, opacity: 0 }}
              initial={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Admin Notes
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                These notes are only visible to administrators
              </p>
              <textarea
                className="input w-full h-32 resize-none"
                placeholder="Enter your notes here..."
                value={currentNotes.notes}
                onChange={(e) =>
                  setCurrentNotes({ ...currentNotes, notes: e.target.value })
                }
              />
              <div className="flex gap-3 mt-4">
                <button
                  className="btn btn-ghost flex-1"
                  onClick={() => setShowNotesModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary flex-1"
                  onClick={handleSaveNotes}
                >
                  Save Notes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ResellerLogsModal
        open={showLogsModal}
        onClose={() => setShowLogsModal(false)}
      />

      <ModuleAuditLogsModal
        open={showModuleLogsModal}
        onClose={() => setShowModuleLogsModal(false)}
      />

      {/* Disable Confirmation Modal */}
      <AnimatePresence>
        {showDisableModal && resellerToDisable && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={handleCloseDisableModal}
          >
            <motion.div
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md"
              exit={{ scale: 0.9, opacity: 0 }}
              initial={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center mb-4">
                <div
                  className={`p-3 rounded-full ${
                    resellerToDisable.status === "inactive"
                      ? "bg-green-100 dark:bg-green-900/30"
                      : "bg-orange-100 dark:bg-orange-900/30"
                  }`}
                >
                  <i
                    className={`fas ${
                      resellerToDisable.status === "inactive"
                        ? "fa-check-circle text-green-600 dark:text-green-400"
                        : "fa-ban text-orange-600 dark:text-orange-400"
                    } text-xl`}
                  />
                </div>
                <h2 className="text-xl font-semibold ml-3 text-gray-900 dark:text-white">
                  {resellerToDisable.status === "inactive"
                    ? "Enable Reseller"
                    : "Disable Reseller"}
                </h2>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to{" "}
                {resellerToDisable.status === "inactive" ? "enable" : "disable"}{" "}
                access for{" "}
                <span className="font-semibold">
                  {resellerToDisable.username}
                </span>
                ?
              </p>
              <div className="flex gap-3">
                <button
                  className="btn btn-ghost flex-1"
                  disabled={isDisabling}
                  onClick={handleCloseDisableModal}
                >
                  Cancel
                </button>
                <button
                  className={`btn flex-1 ${
                    resellerToDisable.status === "inactive"
                      ? "btn-success bg-green-600 hover:bg-green-700 text-white"
                      : "btn-warning bg-orange-600 hover:bg-orange-700 text-white"
                  }`}
                  disabled={isDisabling}
                  onClick={handleConfirmDisable}
                >
                  {isDisabling ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2" />
                      {resellerToDisable.status === "inactive"
                        ? "Enabling..."
                        : "Disabling..."}
                    </>
                  ) : resellerToDisable.status === "inactive" ? (
                    "Enable"
                  ) : (
                    "Disable"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Resellers;
